/**
 * Pricing Manager — los cálculos, como funciones puras.
 *
 * Son las closures que vivían dentro de `PricingEditor.tsx` (port del
 * pricing.tsx de Replit): mismos cuerpos, mismos nombres de paso (las
 * referencias BH4/BI7/BS9… son las celdas de la planilla BBDD original), con
 * la única diferencia de que los datos entran por parámetro en vez de leerse
 * del estado del componente. Cada función es determinista y no toca React.
 *
 * Paridad: `backend/src/lib/pricing-calc.test.ts` replica un snapshot de
 * entradas/salidas capturado corriendo las closures ORIGINALES (auditoría
 * 18/09/2026, sprint 4) — si un cambio acá mueve un número, el test lo ve.
 * Antes de tocar una fórmula: cambiar la planilla de referencia y regenerar
 * el snapshot a conciencia, nunca "ajustar" el test.
 */

// ── Tipos (filas del editor y lookups de la planilla) ───────────────────────

export interface OtherCostRow {
  id: string;
  description: string;
  solution: string;
  monthlyCostUSD: number;
}

export interface AssetCostEntry {
  asset: string;
  seniority: string;
  unitCost: number;
}

export interface RoleCostEntry {
  role: string;
  country: string;
  cost: number;
}

export interface FinancingCostEntry {
  paymentTerm: string;
  financingCost: number;
}

export interface CampaignTypeMediaEntry {
  campaignType: string;
  campaignMedia: string;
}

export interface ContentLabelValue { label: string; value: number; }
export interface ContentAssetEquivEntry { assetType: string; equivalent: number; }
export interface ContentSsrLookupEntry { complexity: string; minValue: number; }
export interface ContentCalcConstants {
  bp8: number; bp10: number; bp13: number;
  bp44: number; bp46: number; bp49: number;
  br6: number; bs6: number; bs9: number; bs10: number;
  bu19: number; bu20: number; bu21: number;
  br42: number; bs42: number; bs45: number; bs46: number;
  bu55: number; bu56: number; bu57: number;
}
export interface ContentAllocations {
  cpAnalyst: number | null;
  cpCoord: number | null;
  csSsr: number | null;
  csCoord: number | null;
}

export interface SuggestedTeamConstants { bh4: number; bi4: number; bi7: number; bi8: number; bi9: number; bi10: number }

export interface TeamRow {
  id: string;
  role: string;
  country: string;
  group: string;
  allocation: number;
}

export interface AssetRow {
  id: string;
  asset: string;
  seniority: string;
  unitCost: number | null;
  group: string;
  totalQuantity: number;
}

export interface PMLCampaignRow {
  id: string;
  campaignName: string;
  campaignType: string;
  campaignMedia: string;
  campaignsQuantity: number;
}

export interface PMLSourcingRow {
  id: string;
  country: string;
  role: string;
  allocation: number;
}

export interface PMLSuggestedTeam {
  srAnalystAllocation: number | null;
  leadAllocation: number | null;
}

export interface ContentAssetRow {
  id: string;
  assetType: string;
  assetQuantity: number;
}

export interface ContentTeamRow {
  role: string;
  country: string;
  allocationOrPrice: number | null;
}

export interface ContentVariables {
  clientGeos: string;
  clientComplexity: string;
  creatorAdvocateComplexity?: string;
}

export interface CreatorAdvocateDetailRow {
  id: string;
  country: string;
  followersTier: string;
  quantity: number;
}

export interface ContentCreatorAdvocateDetailData {
  creators: CreatorAdvocateDetailRow[];
  advocates: CreatorAdvocateDetailRow[];
}

export interface DesiredMarginEntry {
  solution: string;
  margin: number;
  suggestedPricing: number | null;
}

export interface DesiredPricingEntry {
  solution: string;
  desiredPricing: number | null;
  obtainedMargin: number | null;
}

/** Subconjunto de la planilla (SheetData) que usan los cálculos. Todo opcional: el editor arranca sin planilla. */
export interface PricingLookups {
  roleCostLookup?: RoleCostEntry[];
  financingCostLookup?: FinancingCostEntry[];
  // PML
  campaignTypeValueLookup?: Array<{ campaignType: string; value: number }>;
  complexityClientLookup?: Array<{ key: string; value: number }>;
  mediaValueLookup?: Array<{ key: string; value: number }>;
  kpi2MediaValueLookup?: Array<{ key: string; value: number }>;
  kpi2ComplexityLookup?: Array<{ key: string; value: number }>;
  suggestedTeamConstants?: SuggestedTeamConstants;
  // Content
  contentCalcConstants?: ContentCalcConstants;
  contentCpAssetLookup?: ContentAssetEquivEntry[];
  contentCsAssetLookup?: ContentAssetEquivEntry[];
  contentCpGeosLookup?: ContentLabelValue[];
  contentCpComplexityLookup?: ContentLabelValue[];
  contentCpClientComplexityLookup?: ContentLabelValue[];
  contentCsGeosLookup?: ContentLabelValue[];
  contentCsComplexityLookup?: ContentLabelValue[];
  contentCpSsrLookup?: ContentSsrLookupEntry[];
  contentCsSsrLookup?: ContentSsrLookupEntry[];
  contentCreatorAdvocateLookup?: Array<{ country: string; complexity: string; unitPrice: number; roleType: string }>;
}

export const PML_SOLUTION = 'Paid Media Lab';
export const CONTENT_SOLUTION = 'Content';

// ── Financiación ────────────────────────────────────────────────────────────

/**
 * Multiplicador por condición de pago. La planilla lo aplica solo si es > 0;
 * si no hay término, lookup o entrada, el costo queda igual (×1).
 */
export function financingMultiplier(lookup: FinancingCostEntry[] | undefined, paymentTerms: string | null | undefined): number {
  if (paymentTerms && lookup) {
    const fe = lookup.find(e => e.paymentTerm === paymentTerms);
    if (fe && fe.financingCost > 0) return fe.financingCost;
  }
  return 1;
}

// ── Paid Media Lab ──────────────────────────────────────────────────────────

export interface PmlCostInputs {
  roleCostLookup?: RoleCostEntry[];
  pmlSourcingRows: PMLSourcingRow[];
  otherCostRows: OtherCostRow[];
  durationMonths: number;
}

/** Roles del formato viejo (Replit) de `pmlSourcingTeamData`: un objeto con un país y 4 allocations fijas. */
const PML_LEGACY_ROLES: Array<{ key: string; role: string }> = [
  { key: 'analystAllocation', role: 'Performance Specialist' },
  { key: 'srAnalystAllocation', role: 'Performance Specialist Sr' },
  { key: 'strategistAllocation', role: 'Performance Lead/Strategist' },
  { key: 'leadAllocation', role: 'Performance Lead/Strategist' },
];

/**
 * `pmlSourcingTeamData` como lo guarda cada época: el editor del BO guarda un ARRAY de filas; las
 * versiones importadas de Replit traen un OBJETO `{country, analystAllocation, …}`. Devuelve
 * siempre filas (las del objeto viejo solo con allocation > 0). Lo usan el editor al cargar una
 * versión y el reporte de Google Sheets: una sola lectura de los dos formatos.
 */
export function normalizePmlSourcing(data: unknown, makeId: (i: number) => string = (i) => `pml-${i}`): PMLSourcingRow[] {
  if (Array.isArray(data)) return data as PMLSourcingRow[];
  if (!data || typeof data !== 'object') return [];
  const old = data as Record<string, unknown>;
  const country = typeof old.country === 'string' ? old.country : '';
  const rows: PMLSourcingRow[] = [];
  for (const { key, role } of PML_LEGACY_ROLES) {
    const allocation = Number(old[key]) || 0;
    if (allocation > 0) rows.push({ id: makeId(rows.length), country, role, allocation });
  }
  return rows;
}

/** Costo de una fila de sourcing de PML (rol × país × % × meses); 0 si está incompleta o el rol no tiene costo. */
export function pmlSourcingRowCost(row: PMLSourcingRow, roleCostLookup: RoleCostEntry[] | undefined, durationMonths: number): number {
  if (!roleCostLookup || !(row.allocation > 0 && row.role && row.country)) return 0;
  const costEntry = roleCostLookup.find(entry => entry.role === row.role && entry.country === row.country);
  return costEntry ? costEntry.cost * (row.allocation / 100) * durationMonths : 0;
}

export interface CostBreakdown {
  team: number;
  assets: number;
  other: number;
  /** team + assets + other, sin financiación. */
  base: number;
  /** total − base. */
  financing: number;
  /** base × multiplicador de financiación. */
  total: number;
}

const withFinancing = (team: number, assets: number, other: number, multiplier: number): CostBreakdown => {
  const base = team + assets + other;
  const total = base * multiplier;
  return { team, assets, other, base, financing: total - base, total };
};

/** Desglose de PML: sourcing + otros costos de la solución, con financiación. */
export function pmlCostBreakdown(i: PmlCostInputs & { financingCostLookup?: FinancingCostEntry[]; paymentTerms?: string | null }): CostBreakdown {
  if (!i.roleCostLookup) return withFinancing(0, 0, 0, 1);
  let team = 0;
  for (const row of i.pmlSourcingRows) team += pmlSourcingRowCost(row, i.roleCostLookup, i.durationMonths);
  let other = 0;
  i.otherCostRows
    .filter(row => row.solution === PML_SOLUTION)
    .forEach(row => { other += (row.monthlyCostUSD || 0) * i.durationMonths; });
  return withFinancing(team, 0, other, financingMultiplier(i.financingCostLookup, i.paymentTerms));
}

/** Costo base de PML: sourcing (rol × país × % × meses) + otros costos de la solución. Sin financiación. */
export function pmlBaseCost(i: PmlCostInputs): number {
  return pmlCostBreakdown(i).base;
}

/** Costo total de PML con financiación. */
export function pmlTotalCost(i: PmlCostInputs & { financingCostLookup?: FinancingCostEntry[]; paymentTerms?: string | null }): number {
  const baseCost = pmlBaseCost(i);
  return baseCost * financingMultiplier(i.financingCostLookup, i.paymentTerms);
}

export interface PmlSuggestedTeamInputs {
  sheet: PricingLookups | null | undefined;
  pmlCampaignRows: PMLCampaignRow[];
  pmlComplexity: string;
}

/**
 * Equipo sugerido de PML (Sr Analyst y Lead, en %). `null` = no se puede
 * calcular (faltan lookups o complejidad) y el editor limpia lo sugerido.
 */
export function pmlSuggestedTeam(i: PmlSuggestedTeamInputs): PMLSuggestedTeam | null {
  const sheetData = i.sheet;
  const { pmlCampaignRows, pmlComplexity } = i;
  if (!sheetData?.campaignTypeValueLookup || !sheetData?.complexityClientLookup ||
      !sheetData?.mediaValueLookup || !sheetData?.kpi2MediaValueLookup ||
      !sheetData?.kpi2ComplexityLookup || !sheetData?.suggestedTeamConstants || !pmlComplexity) {
    return null;
  }

  try {
    const constants = sheetData.suggestedTeamConstants;

    // Step 1: CAMP VALUE - sum of (campaign type value × campaigns quantity) for each row
    let campValue = 0;
    for (const row of pmlCampaignRows) {
      if (row.campaignType && row.campaignsQuantity > 0) {
        const entry = sheetData.campaignTypeValueLookup.find(e => e.campaignType === row.campaignType);
        if (entry) {
          campValue += entry.value * row.campaignsQuantity;
        }
      }
    }

    // Step 2: MEDIA (Q) - count unique campaign medias
    const uniqueMedias = new Set<string>();
    for (const row of pmlCampaignRows) {
      if (row.campaignMedia) {
        uniqueMedias.add(row.campaignMedia);
      }
    }
    const mediaQ = uniqueMedias.size;

    // Step 3: MEDIA (VALUE) - lookup media count in BB19:BC27
    const mediaValueEntry = sheetData.mediaValueLookup.find(e => e.key === String(mediaQ));
    const mediaValue = mediaValueEntry?.value || 0;

    // Step 4: COMPLEXITY CLIENT - lookup selected complexity in BB13:BC16
    const complexityEntry = sheetData.complexityClientLookup.find(e => e.key === pmlComplexity);
    const complexityClient = complexityEntry?.value || 0;

    // Step 5: OPP VALUE = CAMP VALUE × MEDIA (VALUE) × COMPLEXITY CLIENT
    const oppValue = campValue * mediaValue * complexityClient;

    // Step 6: KPI2 - MEDIA VALUE - lookup media count in BE5:BF14
    const kpi2MediaEntry = sheetData.kpi2MediaValueLookup.find(e => e.key === String(mediaQ));
    const kpi2MediaValue = kpi2MediaEntry?.value || 0;

    // Step 7: KPI2 - COMPLEXITY - lookup complexity in BE29:BF32
    const kpi2ComplexityEntry = sheetData.kpi2ComplexityLookup.find(e => e.key === pmlComplexity);
    const kpi2Complexity = kpi2ComplexityEntry?.value || 0;

    // Step 8: SR ANALYST
    // =IFERROR(IF(OPP VALUE<BI7, IF(OPP VALUE/BH4<BI9, BI9, OPP VALUE/BH4), (BI7/BH4) + (OPP VALUE-BI7)*BI8/BH4), "-")
    let srAnalyst: number | null = null;
    if (constants.bh4 !== 0) {
      if (oppValue < constants.bi7) {
        const calc = oppValue / constants.bh4;
        srAnalyst = calc < constants.bi9 ? constants.bi9 : calc;
      } else {
        srAnalyst = (constants.bi7 / constants.bh4) + (oppValue - constants.bi7) * constants.bi8 / constants.bh4;
      }
    }

    // Step 9: CLIENT VALUE = ((KPI2 - MEDIA VALUE)*0.5) + ((KPI2 - COMPLEXITY)*0.5) + SR ANALYST
    const clientValue = (kpi2MediaValue * 0.5) + (kpi2Complexity * 0.5) + (srAnalyst || 0);

    // Step 10: LEAD
    // =IFERROR(IF(OPP VALUE/BH4 <= BI9, BI10, CLIENT VALUE/BI4), "-")
    let lead: number | null = null;
    if (constants.bh4 !== 0 && constants.bi4 !== 0) {
      if (oppValue / constants.bh4 <= constants.bi9) {
        lead = constants.bi10;
      } else {
        lead = clientValue / constants.bi4;
      }
    }

    return {
      srAnalystAllocation: srAnalyst !== null ? Math.round(srAnalyst * 10000) / 100 : null,
      leadAllocation: lead !== null ? Math.round(lead * 10000) / 100 : null,
    };
  } catch (error) {
    console.error('Error calculating suggested team:', error);
    return null;
  }
}

// ── Soluciones "clásicas" (equipo + assets + otros) ─────────────────────────

export interface SolutionCostInputs {
  roleCostLookup?: RoleCostEntry[];
  financingCostLookup?: FinancingCostEntry[];
  teamRows: TeamRow[];
  assetRows: AssetRow[];
  otherCostRows: OtherCostRow[];
  durationMonths: number;
  paymentTerms?: string | null;
}

/** Costo de una fila de equipo (% × costo mensual del rol en el país × meses); un rol sin costo vale 0. */
export function teamRowCost(row: Pick<TeamRow, 'role' | 'country' | 'allocation'>, roleCostLookup: RoleCostEntry[], durationMonths: number): number {
  const roleCostEntry = roleCostLookup.find(entry => entry.role === row.role && entry.country === row.country);
  const monthlyCost = roleCostEntry?.cost || 0;
  return (row.allocation / 100) * monthlyCost * durationMonths;
}

/** Costo de una fila de assets (cantidad total × costo unitario). */
export function assetRowCost(row: Pick<AssetRow, 'totalQuantity' | 'unitCost'>): number {
  return (row.totalQuantity || 0) * (row.unitCost || 0);
}

/** Desglose de una solución (equipo, assets, otros costos, financiación). Lo usan el editor (vía `solutionCost`) y el reporte. */
export function solutionCostBreakdown(i: SolutionCostInputs, solution: string): CostBreakdown {
  if (!i.roleCostLookup) return withFinancing(0, 0, 0, 1);
  const roleCostLookup = i.roleCostLookup;

  let teamCost = 0;
  i.teamRows
    .filter(row => row.group === solution)
    .forEach(row => { teamCost += teamRowCost(row, roleCostLookup, i.durationMonths); });

  let assetCost = 0;
  i.assetRows
    .filter(row => row.group === solution)
    .forEach(row => { assetCost += assetRowCost(row); });

  let otherCost = 0;
  i.otherCostRows
    .filter(row => row.solution === solution)
    .forEach(row => {
      otherCost += (row.monthlyCostUSD || 0) * i.durationMonths;
    });

  return withFinancing(teamCost, assetCost, otherCost, financingMultiplier(i.financingCostLookup, i.paymentTerms));
}

/** Costo total de una solución (equipo + assets + otros costos) con financiación. */
export function solutionCost(i: SolutionCostInputs, solution: string): number {
  if (!i.roleCostLookup) return 0;
  return solutionCostBreakdown(i, solution).total;
}

// ── Content ─────────────────────────────────────────────────────────────────

export interface ContentAllocationInputs {
  sheet: PricingLookups | null | undefined;
  contentVariables: ContentVariables;
  contentAssetRows: ContentAssetRow[];
}

/**
 * Allocations del equipo de Content según las fórmulas de la BBDD. `null` si
 * faltan constantes, variables del cliente o algún lookup.
 */
export function contentAllocations(i: ContentAllocationInputs): ContentAllocations | null {
  const sheetData = i.sheet;
  const C = sheetData?.contentCalcConstants;
  if (!C) return null;

  const { clientGeos, clientComplexity } = i.contentVariables;
  if (!clientGeos || !clientComplexity) return null;

  const lookupVal = (arr: ContentLabelValue[] | undefined, label: string): number | null => {
    if (!arr) return null;
    const e = arr.find(x => x.label === label);
    return e ? e.value : null;
  };
  const lookupSsrMin = (arr: ContentSsrLookupEntry[] | undefined, complexity: string): number | null => {
    if (!arr) return null;
    const e = arr.find(x => x.complexity === complexity);
    return e ? e.minValue : null;
  };
  const pct = (v: number | null): number | null =>
    v !== null ? Math.round(v * 10000) / 100 : null;

  // Step 1: total asset equivalents
  let totalEqCp = 0;
  let totalEqCs = 0;
  for (const row of i.contentAssetRows) {
    if (!row.assetType || row.assetQuantity <= 0) continue;
    const cp = sheetData?.contentCpAssetLookup?.find(e => e.assetType === row.assetType);
    if (cp) totalEqCp += cp.equivalent * row.assetQuantity;
    const cs = sheetData?.contentCsAssetLookup?.find(e => e.assetType === row.assetType);
    if (cs) totalEqCs += cs.equivalent * row.assetQuantity;
  }

  // Step 2: geos & complexity value lookups
  const geosValCp = lookupVal(sheetData?.contentCpGeosLookup, clientGeos);
  const complexValCp = lookupVal(sheetData?.contentCpComplexityLookup, clientComplexity);
  const clientComplexValCp = lookupVal(sheetData?.contentCpClientComplexityLookup, clientComplexity);
  const geosValCs = lookupVal(sheetData?.contentCsGeosLookup, clientGeos);
  const complexValCs = lookupVal(sheetData?.contentCsComplexityLookup, clientComplexity);

  if (geosValCp === null || complexValCp === null || clientComplexValCp === null ||
      geosValCs === null || complexValCs === null) return null;

  // Step 3: KPI Op Values
  const kpiCpOp = totalEqCp * geosValCp * complexValCp;
  const kpiCsOp = totalEqCs * geosValCs * complexValCs;

  // Step 4: CP Ssr Aux — IF(kpiCpOp < BS9, kpiCpOp/BR6, (BS9/BR6) + (kpiCpOp-BS9)*BS10/BR6)
  let cpSsrAux = 0;
  if (C.br6 !== 0) {
    cpSsrAux = kpiCpOp < C.bs9
      ? kpiCpOp / C.br6
      : (C.bs9 / C.br6) + (kpiCpOp - C.bs9) * C.bs10 / C.br6;
  }

  // Step 5: CP Ssr = max(cpSsrAux, lookup minimum for complexity)
  const cpSsrMin = lookupSsrMin(sheetData?.contentCpSsrLookup, clientComplexity);
  const cpSsr = cpSsrMin !== null ? Math.max(cpSsrAux, cpSsrMin) : cpSsrAux;

  // Step 6: KPI CP Client Value = (BP8 × geosValCp + BP10 × clientComplexValCp) × cpSsr
  const kpiCpClient = (C.bp8 * geosValCp + C.bp10 * clientComplexValCp) * cpSsr;

  // Step 7: CP Coord = kpiCpClient / BS6
  const cpCoordRaw = C.bs6 !== 0 ? kpiCpClient / C.bs6 : 0;

  // Step 8: CP Cost Aux = IF((cpSsrAux+cpSsr+cpCoord)=0, null, cpSsr*BU20 + cpCoord*BU21)
  const cpSumCheck = cpSsrAux + cpSsr + cpCoordRaw;
  const cpCostAux = cpSumCheck === 0 ? null : (cpSsr * C.bu20) + (cpCoordRaw * C.bu21);

  // Step 9: CP Analyst = ((cpCostAux - cpCoord*BU21) * BP13) / BU19
  const cpAnalystRaw = (cpCostAux !== null && C.bu19 !== 0)
    ? ((cpCostAux - cpCoordRaw * C.bu21) * C.bp13) / C.bu19
    : null;

  // Step 10: CS Ssr Aux — IF(kpiCsOp < BR42, kpiCsOp/BR42, (BS45/BR42) + (kpiCsOp-BS45)*BS46/BR42)
  let csSsrAux = 0;
  if (C.br42 !== 0) {
    csSsrAux = kpiCsOp < C.br42
      ? kpiCsOp / C.br42
      : (C.bs45 / C.br42) + (kpiCsOp - C.bs45) * C.bs46 / C.br42;
  }

  // Step 11: CS Ssr = max(csSsrAux, lookup minimum for complexity)
  const csSsrMin = lookupSsrMin(sheetData?.contentCsSsrLookup, clientComplexity);
  const csSsr = csSsrMin !== null ? Math.max(csSsrAux, csSsrMin) : csSsrAux;

  // Step 12: KPI CS Client Value = (BP44 × geosValCs + BP46 × complexValCs) × csSsr
  const kpiCsClient = (C.bp44 * geosValCs + C.bp46 * complexValCs) * csSsr;

  // Step 13: CS Coord = IF(kpiCsClient=0, null, kpiCsClient / BS42)
  const csCoordRaw = (kpiCsClient === 0 || C.bs42 === 0) ? null : kpiCsClient / C.bs42;

  // Step 14: CS Cost Aux = IF((csSsrAux+csSsr+csCoord)=0, null, csSsr*BU56 + csCoord*BU57)
  // Step 15: CS Analyst = IF(csSsr=0, null, csCostAux*BP49/BU55)
  // Ambos quedan en la planilla como referencia: no alimentan ninguna fila del equipo.

  return {
    cpAnalyst: pct(cpAnalystRaw),
    cpCoord:   pct(cpCoordRaw),
    csSsr:     pct(csSsr),
    csCoord:   csCoordRaw !== null ? pct(csCoordRaw) : null,
  };
}

/** Vuelca las allocations calculadas sobre las filas del equipo de Content (por rol). */
export function applyAllocationsToRows(rows: ContentTeamRow[], allocs: ContentAllocations): ContentTeamRow[] {
  return rows.map(row => {
    if (row.role === "Content Partner")         return { ...row, allocationOrPrice: allocs.cpAnalyst };
    if (row.role === "Content Partner Lead")    return { ...row, allocationOrPrice: allocs.cpCoord };
    if (row.role === "Content Specialist Sr")   return { ...row, allocationOrPrice: allocs.csSsr };
    if (row.role === "Content Specialist Lead") return { ...row, allocationOrPrice: allocs.csCoord };
    return row;
  });
}

export interface ContentCostInputs {
  sheet: PricingLookups | null | undefined;
  contentTeamRows: ContentTeamRow[];
  contentAssetRows: ContentAssetRow[];
  contentCreatorAdvocateDetailData: ContentCreatorAdvocateDetailData;
  durationMonths: number;
  paymentTerms?: string | null;
}

/** Costo de una fila del equipo de Content (% o precio × costo del rol en el país × meses); 0 si está incompleta. */
export function contentTeamRowCost(row: ContentTeamRow, roleCostLookup: RoleCostEntry[] | undefined, durationMonths: number): number {
  if (row.allocationOrPrice === null || row.allocationOrPrice === undefined) return 0;
  if (!row.country || !roleCostLookup) return 0;
  const entry = roleCostLookup.find(e => e.role === row.role && e.country === row.country);
  return entry ? (row.allocationOrPrice / 100) * entry.cost * durationMonths : 0;
}

export interface ContentCosts {
  teamCost: number;
  creatorsCost: number;
  totalCost: number;
  /** Creators + advocates SIN financiación (lo que se guarda en la versión). */
  rawCreatorsCost: number;
}

/**
 * Costos de Content: equipo (con las allocations frescas si se pasan, para no
 * depender del estado viejo) + creators/advocates, con financiación.
 */
export function contentCosts(i: ContentCostInputs, allocs?: ContentAllocations): ContentCosts {
  const sheetData = i.sheet;
  const rows = allocs ? applyAllocationsToRows(i.contentTeamRows, allocs) : i.contentTeamRows;

  let teamCost = 0;
  let creatorsCost = 0;
  rows.forEach(row => { teamCost += contentTeamRowCost(row, sheetData?.roleCostLookup, i.durationMonths); });

  // Only count creator cost if the "Creator Video - Creación" asset is actually present with qty > 0
  const creatorsAssetQty = i.contentAssetRows
    .filter(r => r.assetType === "Creator Video - Creación")
    .reduce((s, r) => s + (r.assetQuantity || 0), 0);
  if (creatorsAssetQty > 0) {
    i.contentCreatorAdvocateDetailData.creators.forEach(row => {
      if (!row.country || !row.followersTier || !row.quantity) return;
      const entry = sheetData?.contentCreatorAdvocateLookup?.find(
        e => e.roleType === "Creators" && e.country === row.country && e.complexity === row.followersTier
      );
      if (entry) creatorsCost += entry.unitPrice * row.quantity * i.durationMonths;
    });
  }

  // Only count advocate cost if the "Advocates - Creación" asset is actually present with qty > 0
  const advocatesAssetQty = i.contentAssetRows
    .filter(r => r.assetType === "Advocates - Creación")
    .reduce((s, r) => s + (r.assetQuantity || 0), 0);
  if (advocatesAssetQty > 0) {
    i.contentCreatorAdvocateDetailData.advocates.forEach(row => {
      if (!row.country || !row.followersTier || !row.quantity) return;
      const entry = sheetData?.contentCreatorAdvocateLookup?.find(
        e => e.roleType === "Advocates" && e.country === row.country && e.complexity === row.followersTier
      );
      if (entry) creatorsCost += entry.unitPrice * row.quantity * i.durationMonths;
    });
  }

  const baseCost = teamCost + creatorsCost;
  const multiplier = financingMultiplier(sheetData?.financingCostLookup, i.paymentTerms);
  return {
    teamCost: teamCost * multiplier,
    creatorsCost: creatorsCost * multiplier,
    totalCost: baseCost * multiplier,
    rawCreatorsCost: creatorsCost,
  };
}

// ── Márgenes, pricing sugerido y bundle ─────────────────────────────────────

export interface BundleMetrics {
  suggestedTotal: number | null;
  marginFromSuggested: number | null;
  desiredTotal: number | null;
  obtainedFromDesired: number | null;
}

export interface PricingMetrics {
  margins: DesiredMarginEntry[];
  pricings: DesiredPricingEntry[];
  /** Solo con más de una solución; si no, todo null. */
  bundle: BundleMetrics;
}

export const EMPTY_BUNDLE: BundleMetrics = { suggestedTotal: null, marginFromSuggested: null, desiredTotal: null, obtainedFromDesired: null };

/**
 * Margen deseado → pricing sugerido, pricing deseado → margen obtenido, y los
 * agregados del bundle. `costs` es el costo total por solución (ya con
 * financiación); una solución sin costo (0) no sugiere pricing.
 */
export function pricingMetrics(i: {
  solutions: string[];
  costs: Record<string, number>;
  desiredMargins: DesiredMarginEntry[];
  desiredPricings: DesiredPricingEntry[];
}): PricingMetrics {
  const costOf = (solution: string) => i.costs[solution] || 0;

  // Calculate Desired Margin -> Suggested Pricing
  const margins = i.solutions.map((solution) => {
    const existingMargin = i.desiredMargins.find(m => m.solution === solution);
    const marginPercent = existingMargin?.margin || 0;
    const totalCost = costOf(solution);
    let suggestedPricing = 0;
    if (marginPercent < 100 && totalCost > 0) {
      suggestedPricing = totalCost / (1 - marginPercent / 100);
    }
    return {
      solution,
      margin: marginPercent,
      suggestedPricing: totalCost > 0 ? suggestedPricing : null,
    };
  });

  // Calculate Desired Pricing -> Obtained Margin
  const pricings = i.solutions.map((solution) => {
    const existingPricing = i.desiredPricings.find(p => p.solution === solution);
    const desiredPrice = existingPricing?.desiredPricing || 0;
    const totalCost = costOf(solution);
    let obtainedMargin: number | null = null;
    if (desiredPrice > 0 && totalCost > 0) {
      obtainedMargin = ((desiredPrice - totalCost) / desiredPrice) * 100;
    }
    return {
      solution,
      desiredPricing: desiredPrice,
      obtainedMargin,
    };
  });

  // Compute Bundle Pricing results (only when multiple solutions)
  let bundle: BundleMetrics = EMPTY_BUNDLE;
  if (i.solutions.length > 1) {
    const bundleTotalCost = i.solutions.reduce((sum, sol) => sum + (i.costs[sol] || 0), 0);

    // From Desired Margin: sum of suggested pricings → derived margin
    const suggestedSum = margins.reduce((sum, m) => sum + (m.suggestedPricing ?? 0), 0);
    const hasSuggested = margins.some(m => m.suggestedPricing != null && m.suggestedPricing > 0);

    // From Desired Pricing: sum of desired pricings → derived obtained margin
    const desiredSum = pricings.reduce((sum, p) => sum + (p.desiredPricing ?? 0), 0);
    const hasDesired = pricings.some(p => p.desiredPricing != null && p.desiredPricing > 0);

    bundle = {
      suggestedTotal: hasSuggested ? suggestedSum : null,
      marginFromSuggested:
        hasSuggested && suggestedSum > 0 && bundleTotalCost > 0
          ? ((suggestedSum - bundleTotalCost) / suggestedSum) * 100
          : null,
      desiredTotal: hasDesired ? desiredSum : null,
      obtainedFromDesired:
        hasDesired && desiredSum > 0 && bundleTotalCost > 0
          ? ((desiredSum - bundleTotalCost) / desiredSum) * 100
          : null,
    };
  }

  return { margins, pricings, bundle };
}

// ── Orquestador: lo que hace "Recalcular" en el editor, sin React ───────────

export interface RecalculateInputs extends PricingLookupsHolder {
  durationMonths: number;
  paymentTerms?: string | null;
  /** Soluciones "clásicas" seleccionadas (sin PML ni Content). */
  teamAssetsSolutions: string[];
  isPmlSelected: boolean;
  isContentSelected: boolean;
  teamRows: TeamRow[];
  assetRows: AssetRow[];
  otherCostRows: OtherCostRow[];
  pmlSourcingRows: PMLSourcingRow[];
  pmlCampaignRows: PMLCampaignRow[];
  pmlComplexity: string;
  contentVariables: ContentVariables;
  contentAssetRows: ContentAssetRow[];
  contentTeamRows: ContentTeamRow[];
  contentCreatorAdvocateDetailData: ContentCreatorAdvocateDetailData;
  desiredMargins: DesiredMarginEntry[];
  desiredPricings: DesiredPricingEntry[];
}
interface PricingLookupsHolder { sheet: PricingLookups | null | undefined }

export interface RecalculateResult {
  /** Soluciones con métricas, en el orden del editor (clásicas, luego PML, luego Content). */
  solutions: string[];
  /** Costo total por solución, con financiación. */
  costs: Record<string, number>;
  metrics: PricingMetrics;
  /** `undefined` si Content no está seleccionado. */
  contentAllocations: ContentAllocations | null | undefined;
  contentCosts: ContentCosts | null;
  /** `undefined` si PML no está seleccionado; `null` si no se pudo calcular (el editor limpia). */
  pmlSuggestedTeam: PMLSuggestedTeam | null | undefined;
}

export function recalculatePricing(i: RecalculateInputs): RecalculateResult {
  const sheet = i.sheet;
  const base = {
    roleCostLookup: sheet?.roleCostLookup,
    financingCostLookup: sheet?.financingCostLookup,
    otherCostRows: i.otherCostRows,
    durationMonths: i.durationMonths,
    paymentTerms: i.paymentTerms,
  };

  // Compute fresh Content allocations (before state update, so we can use for cost)
  const contentAllocs = i.isContentSelected
    ? contentAllocations({ sheet, contentVariables: i.contentVariables, contentAssetRows: i.contentAssetRows })
    : undefined;
  const freshContentCosts = i.isContentSelected
    ? contentCosts(
        { sheet, contentTeamRows: i.contentTeamRows, contentAssetRows: i.contentAssetRows, contentCreatorAdvocateDetailData: i.contentCreatorAdvocateDetailData, durationMonths: i.durationMonths, paymentTerms: i.paymentTerms },
        contentAllocs ?? undefined,
      )
    : null;

  // Get all solutions that need metrics (including PML and Content if selected)
  const solutions = [...i.teamAssetsSolutions];
  if (i.isPmlSelected && !solutions.includes(PML_SOLUTION)) solutions.push(PML_SOLUTION);
  if (i.isContentSelected && !solutions.includes(CONTENT_SOLUTION)) solutions.push(CONTENT_SOLUTION);

  const costs: Record<string, number> = {};
  for (const solution of solutions) {
    costs[solution] = solution === PML_SOLUTION
      ? pmlTotalCost({ ...base, pmlSourcingRows: i.pmlSourcingRows })
      : solution === CONTENT_SOLUTION
        ? (freshContentCosts?.totalCost ?? 0)
        : solutionCost({ ...base, teamRows: i.teamRows, assetRows: i.assetRows }, solution);
  }

  return {
    solutions,
    costs,
    metrics: pricingMetrics({ solutions, costs, desiredMargins: i.desiredMargins, desiredPricings: i.desiredPricings }),
    contentAllocations: contentAllocs,
    contentCosts: freshContentCosts,
    pmlSuggestedTeam: i.isPmlSelected
      ? pmlSuggestedTeam({ sheet, pmlCampaignRows: i.pmlCampaignRows, pmlComplexity: i.pmlComplexity })
      : undefined,
  };
}
